import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import * as FiIcons from 'react-icons/fi';
const {FiArrowLeft, FiCalendar}=FiIcons;

const AboutPage=()=> {
  return (
    <>
      <SEOHead 
        title="Über Nadine Rau - Lexware Office Expertin | Smarte Buchhaltung Online" 
        description="Lernen Sie Nadine Rau kennen - zertifizierte Lexware Office Coach mit 15 Jahren Erfahrung. Persönliche Betreuung für die Digitalisierung Ihrer Buchhaltung." 
        keywords="nadine rau,lexware office coach,buchhaltungsexpertin,digitale buchhaltung beratung,lexware office zertifiziert,buchhaltung digitalisieren kassel" 
        canonical="https://smarte-buchhaltung.online/#/ueber-mich" 
      />
      <div className="min-h-screen bg-gray-50 pt-20 pb-16">
        <div className="container mx-auto px-6 lg:px-8">
          {/* Header */}
          <motion.div 
            initial={{opacity: 0,y: 30}} 
            animate={{opacity: 1,y: 0}} 
            transition={{duration: 0.6}} 
            className="text-center mb-16"
          >
            <motion.a 
              href="#/" 
              whileHover={{scale: 1.05}} 
              className="inline-flex items-center text-red-500 hover:text-red-600 mb-6"
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2" />
              Zurück zur Startseite
            </motion.a>
          </motion.div>
          
          {/* Full Width Heading */}
          <div className="max-w-6xl mx-auto mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Wer ich bin & wie ich arbeite</h1>
          </div>
          
          {/* About Content */}
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto mb-16">
            {/* Text Column (now first) */}
            <motion.div 
              initial={{opacity: 0,x: -50}} 
              animate={{opacity: 1,x: 0}} 
              transition={{duration: 0.8}} 
              className="space-y-8"
            >
              <div>
                <p className="text-xl text-gray-600 leading-relaxed mb-6">
                  Ich unterstütze Solo-Selbstständige und kleine Unternehmen dabei, ihre Buchhaltung so zu gestalten,
                  dass sie klar, verständlich und machbar ist – ohne Stress und ohne Fachsprache.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Als Unternehmerin weiß ich, wie schnell Buchhaltung zur Belastung werden kann,
                  wenn Abläufe unklar sind oder ständig Zeit fehlt. Gleichzeitig habe ich erfahren,
                  wie viel Ruhe entsteht, wenn Strukturen einfach funktionieren.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Meine Haltung:<br />
                  Ich erkläre verständlich, arbeite Schritt für Schritt und passe die Abläufe an deinen Alltag an.<br />
                  Keine Bewertung. Kein Druck. Einfach gemeinsam machen.
                </p>
              </div>
            </motion.div>
            
            {/* Image Column (now second) */}
            <motion.div 
              initial={{opacity: 0,x: 50}} 
              animate={{opacity: 1,x: 0}} 
              transition={{duration: 0.8}}
            >
              <div className="bg-[#F3F1EE] rounded-2xl p-2">
                <img 
                  src="https://github.com/mike3011/Bilder/blob/main/Bild_Nadine_Rau_freigestellt.png?raw=true" 
                  alt="Portrait von Nadine Rau - Gründerin und Buchhaltungsexpertin" 
                  className="w-full h-96 object-cover rounded-xl"
                />
              </div>
            </motion.div>
          </div>
          
          {/* Information Sections with Background Color */}
          <div className="max-w-6xl mx-auto space-y-8 mb-16">
            <div className="bg-[#F3F1EE] p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Beruflicher Hintergrund</h2>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-[#1A1A1A] mr-2">•</span>
                  <span>seit über 15 Jahren selbstständig</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#1A1A1A] mr-2">•</span>
                  <span>Erfahrung in Unternehmensführung und Personalverantwortung</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#1A1A1A] mr-2">•</span>
                  <span>digitale, praxistaugliche Arbeitsabläufe erfolgreich aufgebaut</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#1A1A1A] mr-2">•</span>
                  <span>Buchhaltung seit über 15 Jahren selbst geführt</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#1A1A1A] mr-2">•</span>
                  <span>Arbeit mit verschiedenen Systemen: DATEV, branchenspezifische Software, Lexware Office</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-[#F3F1EE] p-8 rounded-xl shadow-lg">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Zertifizierungen & Spezialisierung</h2>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-[#1A1A1A] mr-2">•</span>
                      <span>Zertifizierte Lexware Office Expertin & Coach</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#1A1A1A] mr-2">•</span>
                      <span>seit 5 Jahren tägliche Arbeit mit Lexware Office in der eigenen Buchhaltung</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#1A1A1A] mr-2">•</span>
                      <span>Durchführung von digitalen Buchhaltungs-Workshops</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#1A1A1A] mr-2">•</span>
                      <span>Erfahrung in der Übertragung alter Systeme in eine neue digitale Struktur</span>
                    </li>
                  </ul>
                </div>
                <div className="flex items-center justify-center">
                  <img 
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758138845970-image.png" 
                    alt="Lexware Office Zertifizierter Coach" 
                    className="w-32 h-auto"
                  />
                </div>
              </div>
            </div>
            
            <div className="bg-[#F3F1EE] p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Womit ich dir helfen kann</h2>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-[#1A1A1A] mr-2">•</span>
                  <span>digitale Buchhaltung einrichten – Schritt für Schritt</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#1A1A1A] mr-2">•</span>
                  <span>Lexware Office sicher und verständlich bedienen</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#1A1A1A] mr-2">•</span>
                  <span>Routinen aufbauen, die verlässlich im Alltag laufen</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#1A1A1A] mr-2">•</span>
                  <span>typische Stolperstellen vermeiden, weil ich sie aus der Praxis kenne</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#1A1A1A] mr-2">•</span>
                  <span>wieder Ruhe, Klarheit und Überblick in deine Zahlen bringen</span>
                </li>
              </ul>
            </div>
            
            {/* CTA Section with Updated Content - Half width and centered */}
            <div className="max-w-3xl mx-auto w-full">
              <div className="bg-[#F3F1EE] p-8 rounded-xl shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Lass uns sprechen - ganz in Ruhe</h2>
                <p className="text-gray-600 mb-6">
                  Wir schauen gemeinsam, wo du gerade stehst und was dir im Moment am meisten helfen würde.<br />
                  Wir finden zusammen heraus, welcher nächste Schritt sinnvoll ist.
                </p>
                <div className="flex justify-center">
                  <motion.a 
                    href="https://nextcloud.managershop.com/index.php/apps/appointments/pub/Gb05r41McyYVyGQE/form" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    whileHover={{scale: 1.05}} 
                    whileTap={{scale: 0.95}} 
                    className="bg-[#E30613] hover:bg-[#C10511] text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiCalendar} />
                    <span>Gespräch vereinbaren</span>
                  </motion.a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutPage;